/**
 * frontend/dashboards/CreatorDashboard.jsx
 * SoundDAO — Creator (Artist) dashboard — Sprint 4 M3
 *
 * Sections:
 *   1. Stats bar  — total fans, SOUND earned, FReN status badge
 *   2. Balance card (live)
 *   3. Post content card — 4-tab: ✏️ Text · 📷 Photo · 🔗 Link · 🎬 Video
 *   4. Video section card — upload / preview / replace
 *   5. FReN collection status card → opens Ivy chat
 *   6. Street team display
 *   7. AI Missions
 *   8. Notification bell + panel
 *
 * Props:
 *   user       — users row
 *   balance    — initial SOUND balance
 *   session    — Supabase auth session
 *   onSettings — navigate to /settings
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { authFetch } from '../lib/authFetch';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import Chatbot from '../Chatbot';
import MissionCard from '../components/MissionCard';
import StageArc from '../components/StageArc';
import { getDisplayName } from '../utils/displayName';
import { useRates, sound, dropSound } from '../lib/rates';
import './Dashboard.css';
import BrandMark from '../components/BrandMark';
import { inviteUrl } from '../lib/constants';

const API_BASE       = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const VIDEO_MAX_BYTES = 50 * 1024 * 1024; // 50 MB
const PHOTO_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// ── FReN status labels ─────────────────────────────────────────────────────────
const FREN_STATUS_LABEL = {
  not_started:  { label: 'Not Started',  color: 'rgba(255,255,255,0.3)' },
  in_progress:  { label: 'In Progress',  color: '#f6d365' },
  launched:     { label: '🚀 Launched',  color: '#64ffb0' },
};

// ── Platform detector for link posts ──────────────────────────────────────────
function detectPlatform(url) {
  try {
    const h = new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '');
    if (h.includes('spotify.com'))    return 'Spotify';
    if (h.includes('youtube.com') || h.includes('youtu.be')) return 'YouTube';
    if (h.includes('soundcloud.com')) return 'SoundCloud';
    if (h.includes('bandcamp.com'))   return 'Bandcamp';
    if (h.includes('instagram.com'))  return 'Instagram';
    if (h.includes('tiktok.com'))     return 'TikTok';
    if (h.includes('twitter.com') || h.includes('x.com')) return 'Twitter/X';
    return 'Link';
  } catch {
    return 'Link';
  }
}

// ── MilestoneShareCard (Sprint 5B M8) ─────────────────────────────────────────
function MilestoneShareCard({ milestone, onDismiss }) {
  const stageEmoji = {
    hit_flair:    '🎭',
    hit_burning:  '🔥',
    hit_wildfire: '🌪️',
    hit_phoenix:  '🔥',
  };
  const stageLabel = {
    hit_flair:    'FLAIR',
    hit_burning:  'BURNING',
    hit_wildfire: 'WILDFIRE',
    hit_phoenix:  'PHOENIX',
  };

  const emoji = stageEmoji[milestone.milestone_type] || '🎉';
  const label = stageLabel[milestone.milestone_type] || 'MILESTONE';

  function handleShare() {
    const text = encodeURIComponent(milestone.share_text);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    onDismiss(milestone.id);
  }

  function handleDismiss() {
    onDismiss(milestone.id);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1a0a2e 0%, #16213e 100%)',
        border: '1px solid rgba(249,115,22,0.4)',
        borderRadius: '16px',
        padding: '32px 24px',
        maxWidth: '360px',
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>{emoji}</div>
        <div style={{
          fontSize: '11px', letterSpacing: '3px',
          opacity: 0.6, marginBottom: '4px',
        }}>
          YOU JUST HIT
        </div>
        <div style={{
          fontSize: '32px', fontWeight: '800',
          color: '#f97316', marginBottom: '16px',
        }}>
          {label}
        </div>
        <p style={{
          fontSize: '14px', opacity: 0.8,
          marginBottom: '24px', lineHeight: '1.5',
        }}>
          {milestone.share_text}
        </p>
        <button
          onClick={handleShare}
          style={{
            width: '100%', padding: '12px',
            background: '#f97316', border: 'none',
            borderRadius: '8px', color: 'white',
            fontWeight: '700', fontSize: '15px',
            cursor: 'pointer', marginBottom: '10px',
          }}
        >
          Share on X
        </button>
        <button
          onClick={handleDismiss}
          style={{
            width: '100%', padding: '10px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px', color: 'white',
            fontSize: '14px', cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ── Ivy directive helpers (Sprint 5C) ──────────────────────────────────────────
// stageProgress comes from GET /api/creators/:id (`stage_progress`), computed by
// backend/lib/stageGates.js. It names EVERY unmet requirement for the next stage.
// The literal "25" that used to live here silently diverged the moment Season
// Zero moved the Burning gate to 10 — and it only ever named one requirement,
// which is why the directive and the arc below it disagreed.
function getIvyDirective(stage, streakCount, totalFans, dropScheduledAt, stageProgress, rates) {
  // Fan referrals pay the LIVE refer_player amount, released once the fan confirms their email
  // and makes a first share or scout (pending_rewards) — never a hard-coded base rate.
  const perSignup = sound(rates, 'refer_player');
  const remaining = stageProgress?.sentence
    ? stageProgress.sentence.replace(/^They /, 'You ').replace(/ they /g, ' you ')
    : null;

  switch (stage) {
    case 'Flair':
      return remaining
        ? `${remaining} Share your referral link on IG or TikTok — every fan who joins and gets active = ${perSignup} and a new street team member.`
        : `Share your referral link on IG or TikTok — every fan who joins and gets active = ${perSignup} and a new street team member.`;
    case 'Burning':
      return dropScheduledAt
        ? 'Your Drop is live. Engage your street team now for maximum reach.'
        : 'Drop scheduling is live. Schedule a release — your street team gets notified. Share your referral link to grow it before the Drop fires.';
    case 'Wildfire':
      return 'You hit Wildfire. Keep posting and schedule a Drop — FReN collections are coming soon. Share your referral link to keep growing your street team.';
    case 'Phoenix':
      return `You made it. ${totalFans} fans, the machine is running itself. Keep sharing your referral link to stay on top.`;
    default:
      return `Post ${Math.max(0, 3 - streakCount)} more times this week to unlock Flair. Share your referral link with fans — every fan who joins and gets active earns you ${perSignup}.`;
  }
}

function getIvyDirectiveCTA(stage) {
  switch (stage) {
    case 'Burning':  return 'Schedule a Drop';
    // No "Launch FReN" / "View collection": FReN is coming soon, not launched.
    default:         return 'Post now';
  }
}

// ── CreatorDashboard ───────────────────────────────────────────────────────────
export default function CreatorDashboard({ user, balance: initialBalance, session, onSettings }) {
  const user_id  = session?.user?.id || user?.id;
  const navigate = useNavigate();
  const rates    = useRates();

  const [balance,        setBalance]        = useState(initialBalance || 0);
  const [weeklySound,    setWeeklySound]    = useState(0);
  const [frenStatus,     setFrenStatus]     = useState('not_started');
  const [streetTeamData, setStreetTeamData] = useState(null);
  const [postsThisMonth, setPostsThisMonth] = useState(0);
  const [videoUrl,       setVideoUrl]       = useState(null);
  const [artistName,     setArtistName]     = useState('');
  const [loadingData,    setLoadingData]    = useState(true);

  // Sprint 5A — Streak + Vault state
  const [streakCount,       setStreakCount]       = useState(0);
  // hotStreakExpiresAt removed in Sprint 5C — streak shown in hero header
  const [vaultBalance,      setVaultBalance]      = useState(0);
  const [vaultLockedUntil,  setVaultLockedUntil]  = useState(null);
  const [vaultLoginCount,   setVaultLoginCount]   = useState(0);

  // Sprint 5B M5 — Drop scheduling state
  const [creatorStage,     setCreatorStage]     = useState('Raw');
  // Season Zero M4: stage thresholds are SERVED, never hardcoded here.
  // { stage, unmet[], sentence } from GET /api/creators/:id → stage_progress.
  const [stageProgress,    setStageProgress]    = useState(null);
  // Served STAGE_GATES (backend/lib/stageGates.js) — the fallback constants that
  // used to live in StageArc.jsx predated Season Zero. Punch-list item 1.
  const [stageGates,       setStageGates]       = useState(null);
  const [dropScheduledAt,  setDropScheduledAt]  = useState('');
  const [dropTitle,        setDropTitle]        = useState('');
  const [creatorDropAt,    setCreatorDropAt]    = useState(null);  // from creator row
  const [creatorDropTitle, setCreatorDropTitle] = useState(null);  // from creator row
  const [schedulingDrop,   setSchedulingDrop]   = useState(false);
  const [cancellingDrop,   setCancellingDrop]   = useState(false);
  const [dropError,        setDropError]        = useState('');
  const [dropStep,         setDropStep]         = useState(1);       // 1=title/date, 2=content
  const [dropContentType,  setDropContentType]  = useState('');
  const [dropContentBody,  setDropContentBody]  = useState('');

  // Sprint 5C — extra creator data for Ivy + StageArc
  const [totalFans,       setTotalFans]       = useState(0);
  const [contentStreak,   setContentStreak]   = useState(0);
  const [heatScore,       setHeatScore]       = useState(0);

  // Referral link state
  const [creatorCopyDone, setCreatorCopyDone] = useState(false);

  // Content reward cooldown state — post card is always visible; badge updates every minute
  const [cooldownUntil,  setCooldownUntil]  = useState(null);  // Date | null
  const [cooldownLabel,  setCooldownLabel]  = useState('');

  // Video section upload state
  const [videoUploading,  setVideoUploading]  = useState(false);
  const [videoUploadErr,  setVideoUploadErr]  = useState('');
  const [videoUploadDone, setVideoUploadDone] = useState(false);
  const videoInputRef = useRef(null);

  // Post card state
  const [postTab,          setPostTab]          = useState('text');   // 'text'|'photo'|'link'|'video'
  const [postText,         setPostText]         = useState('');
  const [postLink,         setPostLink]         = useState('');
  const [postPhoto,        setPostPhoto]        = useState(null);     // File
  const [postPhotoPreview, setPostPhotoPreview] = useState(null);
  const [postVideoFile,    setPostVideoFile]    = useState(null);     // File
  const [postVideoPreview, setPostVideoPreview] = useState(null);
  const [posting,          setPosting]          = useState(false);
  const [postResult,       setPostResult]       = useState(null);     // { success, sound } | { error }
  const postPhotoRef = useRef(null);
  const postVideoRef = useRef(null);

  // Notification state
  const [notifications,  setNotifications]  = useState([]);
  const [notifUnread,    setNotifUnread]    = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  // Sprint 5B M8 — Milestone share cards
  const pendingMilestonesRef = useRef([]);
  const [currentMilestone, setCurrentMilestone] = useState(null);

  const chatRef = useRef(null);

  // ── Signed-URL file upload helper (Fix 2) ────────────────────────────────────
  const uploadFileViaSignedUrl = useCallback(async (file, bucket) => {
    const ext  = file.name.split('.').pop().toLowerCase();
    const path = `${user_id}/${Date.now()}.${ext}`;

    // 1. Get signed URL from backend
    const signRes = await authFetch(
      `${API_BASE}/api/media/sign-upload?bucket=${encodeURIComponent(bucket)}&path=${encodeURIComponent(path)}`
    );
    if (!signRes.ok) {
      const e = await signRes.json().catch(() => ({}));
      throw new Error(e.error || 'Could not get upload URL');
    }
    const { signedUrl } = await signRes.json();

    // 2. PUT file directly to Supabase Storage via signed URL
    const putRes = await fetch(signedUrl, {
      method:  'PUT',
      headers: { 'Content-Type': file.type },
      body:    file,
    });
    if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);

    // 3. Derive public URL (same pattern Supabase uses)
    const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
    return publicUrl;
  }, [user_id]);

  // ── Load data ─────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user_id) return;
    setLoadingData(true);
    try {
      // Balance
      const balRes = await authFetch(`${API_BASE}/api/rewards/balance/${user_id}`);
      if (balRes.ok) {
        const d = await balRes.json();
        setBalance(d.sound_balance || 0);
      }

      // Weekly SOUND
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: weekData } = await supabase
        .from('rewards').select('amount').eq('user_id', user_id).gte('created_at', since);
      setWeeklySound((weekData || []).reduce((s, r) => s + Number(r.amount), 0));

      // Creator row (FReN status + cap + video + artist_name + stage data)
      const { data: creatorRow } = await supabase
        .from('creators')
        .select('id, fren_status, video_url, artist_name, stage, drop_scheduled_at, drop_title, total_fans, content_streak, heat_score')
        .eq('user_id', user_id)   // Sprint 6A.1: the creator's own row, by user_id only
        .maybeSingle();
      if (creatorRow) {
        setFrenStatus(creatorRow.fren_status || 'not_started');
        setVideoUrl(creatorRow.video_url || null);
        setArtistName(creatorRow.artist_name || '');
        setCreatorStage(creatorRow.stage || 'Raw');
        setTotalFans(creatorRow.total_fans || 0);
        setContentStreak(creatorRow.content_streak || 0);
        setHeatScore(creatorRow.heat_score || 0);
        setCreatorDropAt(creatorRow.drop_scheduled_at || null);
        setCreatorDropTitle(creatorRow.drop_title || null);
      }

      // Stage gates + next-stage requirements, computed by lib/stageGates.js.
      try {
        const cRes = await authFetch(`${API_BASE}/api/creators/${user_id}`);
        if (cRes.ok) {
          const cd = await cRes.json();
          setStageProgress(cd.stage_progress || null);
          setStageGates(cd.stage_gates || null);
        }
      } catch { /* non-fatal — the directive falls back to generic copy */ }

      // Street team (Sprint 5B M4)
      try {
        const stRes = await authFetch(`${API_BASE}/api/creators/${user_id}/street-team`);
        if (stRes.ok) {
          const stJson = await stRes.json();
          setStreetTeamData(stJson);
        }
      } catch { /* non-fatal */ }

      // Content posts this month (via history endpoint — avoids RLS issues)
      const monthStart = new Date();
      monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
      const histRes = await authFetch(
        `${API_BASE}/api/rewards/history/${user_id}?limit=50`
      );
      if (histRes.ok) {
        const histData = await histRes.json();
        const thisMonth = (histData.rewards || []).filter(
          r => r.action_type === 'creator_content' && new Date(r.created_at) >= monthStart
        ).length;
        setPostsThisMonth(thisMonth);
      }

      // Cooldown check — fetch most recent creator_content reward (Bug 4)
      const latestRes = await authFetch(
        `${API_BASE}/api/rewards/latest?user_id=${user_id}&action=creator_content`
      );
      if (latestRes.ok) {
        const { reward } = await latestRes.json();
        if (reward?.created_at) {
          const expiry = new Date(new Date(reward.created_at).getTime() + 6 * 60 * 60 * 1000);
          setCooldownUntil(expiry > new Date() ? expiry : null);
        } else {
          setCooldownUntil(null);
        }
      } else {
        setCooldownUntil(null);
      }

      // Sprint 5A — Streak data from users table
      try {
        const { data: streakRow } = await supabase
          .from('users')
          .select('streak_count, hot_streak_expires_at')
          .eq('id', user_id)
          .maybeSingle();
        if (streakRow) {
          setStreakCount(streakRow.streak_count || 0);
          // hot_streak_expires_at no longer displayed — streak count shown in hero
        }
      } catch { /* non-fatal */ }

      // Sprint 5A — Vault data
      try {
        const { data: vaultRow } = await supabase
          .from('vault')
          .select('balance, locked_until, cycle_login_count')
          .eq('user_id', user_id)
          .maybeSingle();
        if (vaultRow) {
          setVaultBalance(vaultRow.balance || 0);
          setVaultLockedUntil(vaultRow.locked_until || null);
          setVaultLoginCount(vaultRow.cycle_login_count || 0);
        }
      } catch { /* non-fatal */ }

      // Notifications
      const notifRes = await authFetch(`${API_BASE}/api/notifications/${user_id}`);
      if (notifRes.ok) {
        const nd = await notifRes.json();
        setNotifications(nd.notifications || []);
        setNotifUnread(nd.unread_count || 0);
      }

      // Sprint 5B M8 — Pending milestone share cards
      if (creatorRow?.id) {
        try {
          const { data: milestones } = await supabase
            .from('milestone_shares')
            .select('id, milestone_type, share_text')
            .eq('creator_id', creatorRow.id)
            .eq('shared', false)
            .order('generated_at', { ascending: true })
            .limit(5);
          pendingMilestonesRef.current = milestones || [];
          if (milestones && milestones.length > 0) {
            setCurrentMilestone(milestones[0]);
          }
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      console.error('[CreatorDashboard] loadData error:', err);
    } finally {
      setLoadingData(false);
    }
  }, [user_id]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Real-time balance ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user_id) return;
    const channel = supabase
      .channel(`creator-balances-${user_id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'balances', filter: `user_id=eq.${user_id}` },
        payload => setBalance(Number(payload.new?.sound_balance || 0))
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user_id]);

  // ── Real-time notifications ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user_id) return;
    const channel = supabase
      .channel(`creator-notifs-${user_id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user_id}` },
        payload => {
          setNotifications(prev => [payload.new, ...prev]);
          setNotifUnread(prev => prev + 1);
        }
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user_id]);

  // ── Cooldown countdown (ticks every minute) ──────────────────────────────────
  useEffect(() => {
    if (!cooldownUntil) { setCooldownLabel(''); return; }
    const tick = () => {
      const ms = cooldownUntil.getTime() - Date.now();
      if (ms <= 0) { setCooldownUntil(null); setCooldownLabel(''); return; }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setCooldownLabel(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  // ── Sprint 5B M8 — Dismiss milestone share card ──────────────────────────────
  const handleMilestoneDismiss = async (milestoneId) => {
    // Mark as shared in DB
    await supabase
      .from('milestone_shares')
      .update({ shared: true })
      .eq('id', milestoneId);

    // Remove from pending and advance to next
    const remaining = pendingMilestonesRef.current.filter(m => m.id !== milestoneId);
    pendingMilestonesRef.current = remaining;
    setCurrentMilestone(remaining.length > 0 ? remaining[0] : null);
  };

  // ── Mark all notifications read ───────────────────────────────────────────────
  const handleMarkAllRead = async () => {
    if (!user_id) return;
    try {
      await authFetch(`${API_BASE}/api/notifications/read-all/${user_id}`, { method: 'PATCH' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setNotifUnread(0);
    } catch (err) {
      console.error('[CreatorDashboard] mark-all-read error:', err);
    }
  };

  // ── Profile video section upload (Fix 2 — signed URL) ────────────────────────
  const handleVideoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoUploadErr('');
    if (file.size > VIDEO_MAX_BYTES) {
      setVideoUploadErr('Video must be 50 MB or less.');
      e.target.value = '';
      return;
    }
    handleVideoUpload(file);
  };

  const handleVideoUpload = useCallback(async (file) => {
    if (!file || !user_id) return;
    setVideoUploading(true);
    setVideoUploadErr('');
    setVideoUploadDone(false);
    try {
      const publicUrl = await uploadFileViaSignedUrl(file, 'creator-videos');

      // Persist via PUT /api/creators/:user_id
      // Prompt 1.5: ownership is proven by the access token.
      const putRes = await authFetch(`${API_BASE}/api/creators/${user_id}`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          artist_name: 'placeholder', // backend preserves existing value
          video_url:   publicUrl,
        }),
      });
      if (!putRes.ok) {
        const errBody = await putRes.json().catch(() => ({}));
        throw new Error(errBody.error || 'Could not save video URL.');
      }
      const { creator } = await putRes.json();
      setVideoUrl(creator.video_url || publicUrl);
      setVideoUploadDone(true);
    } catch (err) {
      console.error('[CreatorDashboard] video upload error:', err);
      setVideoUploadErr(err.message || 'Upload failed. Please try again.');
    } finally {
      setVideoUploading(false);
    }
  }, [user_id, uploadFileViaSignedUrl]);

  // ── Post card — file pickers ──────────────────────────────────────────────────
  const handlePostPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > PHOTO_MAX_BYTES) {
      setPostResult({ error: 'Photo must be 10 MB or less.' });
      e.target.value = '';
      return;
    }
    setPostPhoto(file);
    setPostPhotoPreview(URL.createObjectURL(file));
    setPostResult(null);
  };

  const handlePostVideoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > VIDEO_MAX_BYTES) {
      setPostResult({ error: 'Video must be 50 MB or less.' });
      e.target.value = '';
      return;
    }
    setPostVideoFile(file);
    setPostVideoPreview(URL.createObjectURL(file));
    setPostResult(null);
  };

  // ── Post card — tab switch resets pickers ─────────────────────────────────────
  const handleTabChange = (tab) => {
    setPostTab(tab);
    setPostResult(null);
  };

  // ── Submit post ───────────────────────────────────────────────────────────────
  const handleSubmitPost = useCallback(async () => {
    if (posting) return;
    setPosting(true);
    setPostResult(null);
    try {
      let mediaUrl  = null;
      let content   = null;
      let platform  = null;

      if (postTab === 'text') {
        if (!postText.trim()) {
          setPostResult({ error: 'Write something before posting.' });
          setPosting(false);
          return;
        }
        content = postText.trim();

      } else if (postTab === 'photo') {
        if (!postPhoto) {
          setPostResult({ error: 'Select a photo first.' });
          setPosting(false);
          return;
        }
        mediaUrl = await uploadFileViaSignedUrl(postPhoto, 'creator-photos');
        content  = postText.trim() || null;

      } else if (postTab === 'link') {
        if (!postLink.trim()) {
          setPostResult({ error: 'Paste a link first.' });
          setPosting(false);
          return;
        }
        content  = postLink.trim();
        platform = detectPlatform(content);

      } else if (postTab === 'video') {
        if (!postVideoFile) {
          setPostResult({ error: 'Select a video first.' });
          setPosting(false);
          return;
        }
        mediaUrl = await uploadFileViaSignedUrl(postVideoFile, 'creator-videos');
        content  = postText.trim() || null;
      }

      // Prompt 1.5: the author is the token holder.
      const res = await authFetch(`${API_BASE}/api/posts`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          post_type:  postTab,
          content,
          media_url:  mediaUrl,
          platform,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Post failed');

      // Success — reset form
      setPostText('');
      setPostLink('');
      setPostPhoto(null);
      setPostPhotoPreview(null);
      setPostVideoFile(null);
      setPostVideoPreview(null);
      setPostsThisMonth(prev => prev + 1);
      if (data.sound_awarded > 0) {
        setBalance(prev => prev + data.sound_awarded);
      }
      setPostResult({ success: true, sound: data.sound_awarded });
      try { posthog.capture('content_posted', { post_type: postTab, sound: data.sound_awarded }); } catch (_) { /* analytics non-fatal */ }
      // Enter 6-hour cooldown immediately (badge updates; card stays visible)
      setCooldownUntil(new Date(Date.now() + 6 * 60 * 60 * 1000));
    } catch (err) {
      console.error('[CreatorDashboard] submit post error:', err);
      setPostResult({ error: err.message || 'Something went wrong.' });
    } finally {
      setPosting(false);
    }
  }, [posting, postTab, postText, postLink, postPhoto, postVideoFile, uploadFileViaSignedUrl]);

  // ── Creator referral link (Fix 6) ────────────────────────────────────────────
  const referralCode = user?.referral_code;
  const referralLink = referralCode ? inviteUrl(referralCode) : null;

  const handleCreatorCopyLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCreatorCopyDone(true);
      setTimeout(() => setCreatorCopyDone(false), 2000);
    }).catch(() => {
      window.prompt('Copy your referral link:', referralLink);
    });
  };

  const handleCreatorShareTwitter = () => {
    if (!referralLink) return;
    const text = encodeURIComponent(
      `I'm on @SoundDAO — join my street team and earn SOUND points just for being a fan: ${referralLink}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'noopener');
  };

  const handleCreatorShareEmail = () => {
    if (!referralLink) return;
    const displayName = artistName || getDisplayName(user);
    const subject = encodeURIComponent(
      'Join my street team on SoundDAO — earn crypto just for being a fan'
    );
    const body = encodeURIComponent(
      `Hey,\n\nI'm building my presence on SoundDAO — a platform that actually rewards fans for supporting artists like me.\n\nWhen you join through my link, you earn SOUND points just for signing up. Points count toward SOUND tokens, which are live on testnet today.\n\nJoin here: ${referralLink}\n\nIt takes 30 seconds. No wallet needed — just your email.\n\n${displayName}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // ── Drop scheduling handlers (Sprint 5B M5) ─────────────────────────────────
  const handleScheduleDrop = async () => {
    setSchedulingDrop(true);
    setDropError('');
    try {
      const res = await authFetch(
        `${API_BASE}/api/creators/${user_id}/drops`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id,
            title: dropTitle.trim(),
            scheduled_at: new Date(dropScheduledAt).toISOString(),
            drop_content_type: dropContentType || null,
            drop_content_body: dropContentBody.trim() || null,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setDropError(data.error || 'Failed to schedule drop.');
      } else {
        try { posthog.capture('drop_scheduled', { has_content: !!dropContentType }); } catch (_) { /* analytics non-fatal */ }
        setDropTitle('');
        setDropScheduledAt('');
        setDropStep(1);
        setDropContentType('');
        setDropContentBody('');
        await loadData();
      }
    } catch {
      setDropError('Something went wrong. Try again.');
    } finally {
      setSchedulingDrop(false);
    }
  };

  const handleCancelDrop = async () => {
    setCancellingDrop(true);
    try {
      const { data: dropRow } = await supabase
        .from('drops')
        .select('id')
        .eq('creator_id', user_id)
        .eq('is_live', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dropRow) {
        await authFetch(
          `${API_BASE}/api/creators/${user_id}/drops/${dropRow.id}`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id }),
          }
        );
      }
      await loadData();
    } catch (err) {
      console.error('Cancel drop error:', err);
    } finally {
      setCancellingDrop(false);
    }
  };

  const frenMeta = FREN_STATUS_LABEL[frenStatus] || FREN_STATUS_LABEL.not_started;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard dashboard--creator">

      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-brand"><BrandMark /> SoundDAO <span className="version-badge">Beta v1.0</span></div>
        <div className="dashboard-header-actions">
          <a
            href="https://soundcoin.co/whitepaper"
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-header-link"
            title="Read the White Paper"
          >
            📄 White Paper
          </a>
          <button
            className="dashboard-header-link"
            onClick={() => navigate('/creator-onboarding', { state: { isEditMode: true } })}
            title="Edit Profile"
          >
            ✏️ Edit Profile
          </button>
          <button
            className="dashboard-header-link"
            onClick={() => navigate(`/creator/${user_id}`)}
            title="View public profile"
          >
            👁️ View Profile
          </button>
          <button
            className="dashboard-header-link"
            onClick={() => navigate('/leaderboard')}
            title="Global Leaderboard"
          >
            🏆 Leaderboard
          </button>
          {/* Notification bell */}
          <button
            className="dashboard-notif-btn"
            onClick={() => setShowNotifPanel(p => !p)}
            title="Notifications"
            aria-label={`Notifications${notifUnread > 0 ? ` (${notifUnread} unread)` : ''}`}
          >
            🔔
            {notifUnread > 0 && (
              <span className="dashboard-notif-badge">{notifUnread > 9 ? '9+' : notifUnread}</span>
            )}
          </button>
          <button className="dashboard-settings-btn" onClick={onSettings} title="Settings">⚙️</button>
        </div>
      </header>

      {/* Notification panel */}
      {showNotifPanel && (
        <div className="dashboard-notif-panel" role="dialog" aria-label="Notifications">
          <div className="dashboard-notif-panel-header">
            <h3 className="dashboard-notif-panel-title">Notifications</h3>
            {notifUnread > 0 && (
              <button className="dashboard-notif-mark-read" onClick={handleMarkAllRead}>
                Mark all read
              </button>
            )}
            <button
              className="dashboard-notif-close"
              onClick={() => setShowNotifPanel(false)}
              aria-label="Close"
            >✕</button>
          </div>
          {notifications.length === 0 ? (
            <div className="dashboard-notif-empty">No notifications yet.</div>
          ) : (
            <div className="dashboard-notif-list">
              {notifications.map(n => (
                <div
                  key={n.id}
                  className={`dashboard-notif-item${n.read ? '' : ' dashboard-notif-item--unread'}`}
                >
                  <p className="dashboard-notif-message">{n.message}</p>
                  <p className="dashboard-notif-time">
                    {new Date(n.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Hero (Sprint 5C — artist name + streak + stage) ── */}
      <div className="dashboard-hero">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <h1 className="dashboard-hero-title" style={{ margin: 0 }}>
            {artistName || getDisplayName(user)}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {streakCount > 0 && (
              <span style={{ fontSize: '14px', color: '#f97316', fontWeight: '600' }}>
                🔥 {streakCount}d streak
              </span>
            )}
            {creatorStage && creatorStage !== 'Raw' && (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                background: 'rgba(249,115,22,0.15)',
                border: '1px solid rgba(249,115,22,0.35)',
                borderRadius: '20px', padding: '3px 10px',
                fontSize: '12px', fontWeight: '600', color: '#f97316',
              }}>
                {creatorStage}
              </span>
            )}
          </div>
        </div>
        {/* Sprint 6-Zero M2: creators are identified by STAGE, never a player rank.
            Per the 5C decision the stage badge stays hidden at Raw — the badge
            above already renders for Flair+, so Raw shows artist name + streak
            only and this line renders nothing. */}
        {creatorStage && creatorStage !== 'Raw' && (
          <p className="dashboard-hero-rank" style={{ marginTop: '4px' }}>
            {creatorStage} stage
          </p>
        )}
      </div>


      {/* ══════ Economy Bar (Sprint 5C) ══════ */}
      <div className="economy-bar">
        {/* SOUND balance card */}
        <div className="economy-card economy-card--sound">
          <div className="economy-card-label">SOUND Balance</div>
          <div className="economy-card-value">
            {loadingData ? '—' : balance.toLocaleString()}
          </div>
          <div className="economy-card-weekly">+{weeklySound.toLocaleString()} this week</div>
          <div className="economy-card-sub">
            {creatorStage && creatorStage !== 'Raw' ? `${creatorStage} · ` : ''}
            {postsThisMonth} post{postsThisMonth === 1 ? '' : 's'} this month
          </div>
        </div>
        {/* Vault card */}
        <div className="economy-card economy-card--vault">
          <div className="economy-card-label">Vault</div>
          <div className="economy-card-value">{vaultBalance}</div>
          <div className="economy-card-sub">
            {vaultLockedUntil
              ? `Unlocks in ${Math.max(0, Math.ceil((new Date(vaultLockedUntil) - new Date()) / 86400000))} days`
              : 'SOUND locked'}
          </div>
          <div className="vault-bar">
            <div className="vault-bar-fill" style={{ width: `${Math.min((vaultLoginCount / 30) * 100, 100)}%` }} />
          </div>
          <div className="economy-card-sub">{vaultLoginCount} / 30 days</div>
        </div>
        <div className="economy-bar-footer">
          <span>{totalFans} fans</span>
          <span style={{ color: frenMeta.color }}>{frenMeta.label}</span>
        </div>
      </div>

      {/* Sprint 5B M8 — Milestone share card overlay */}
      {currentMilestone && (
        <MilestoneShareCard
          milestone={currentMilestone}
          onDismiss={handleMilestoneDismiss}
        />
      )}

      {/* ══════ Zone 1 — Ivy Directive (Sprint 5C) ══════ */}
      {artistName ? (
        <div className="ivy-directive-card">
          <div className="ivy-directive-header">
            <div className="ivy-directive-avatar">IV</div>
            <span className="ivy-directive-name">Ivy · Your Publicist</span>
          </div>
          <p className="ivy-directive-message">
            {getIvyDirective(creatorStage, streakCount, totalFans, creatorDropAt, stageProgress, rates)}
          </p>
          <div className="ivy-directive-actions">
            <button className="ivy-btn-primary" onClick={() => {
              const postSection = document.querySelector('.dashboard-post-section');
              if (postSection) postSection.scrollIntoView({ behavior: 'smooth' });
            }}>
              {getIvyDirectiveCTA(creatorStage)}
            </button>
            <button className="ivy-btn-ghost" onClick={() => chatRef.current?.openWithMessage?.('')}>
              Ask Ivy
            </button>
          </div>
        </div>
      ) : (
        <div className="dashboard-empty-state" style={{ textAlign: 'center', padding: '16px' }}>
          <p>Complete your creator profile to unlock Ivy, your AI publicist.</p>
          <button
            className="dashboard-btn dashboard-btn--primary"
            style={{ marginTop: '8px' }}
            onClick={() => navigate('/creator-onboarding', { state: { isEditMode: true } })}
          >
            ✏️ Complete Profile
          </button>
        </div>
      )}

      {/* ── Zone divider ── */}
      <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '8px 0' }} />

      {/* ══════ Zone 2 — Stage Arc + FReN (Sprint 5C) ══════ */}
      {/* Punch-list item 1: gates and the progress sentence are SERVED, never
          hardcoded. stageGates/stageProgress come from GET /api/creators/:id. */}
      <StageArc
        stage={creatorStage}
        totalFans={totalFans}
        heatScore={heatScore}
        contentStreak={contentStreak}
        gates={stageGates}
      />

      {/* FReN collection status card */}
      <div className="dashboard-fren-card">
        <div className="dashboard-fren-header">
          <span className="dashboard-fren-title">🖼️ FReN Collection</span>
          <span className="dashboard-fren-status-badge" style={{ background: frenMeta.color + '22', color: frenMeta.color, border: `1px solid ${frenMeta.color}44` }}>
            {frenMeta.label}
          </span>
        </div>
        <p className="dashboard-fren-desc">
          FReN — Front Row collectibles for fans — coming soon.
        </p>
        <span className="fren-coming-soon">🔜 Coming Soon</span>
      </div>

      {/* ── Zone divider ── */}
      <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '16px 0' }} />

      {/* ══════ Zone 3 — Actions (Sprint 5C reorder) ══════ */}

      {/* 3a. Content posting — always visible; reward badge above tabs */}
      <div className="dashboard-post-section">
        {/* Reward / cooldown badge */}
        <div className={`dashboard-reward-badge ${cooldownUntil ? 'dashboard-reward-badge--cooldown' : 'dashboard-reward-badge--active'}`}>
          {cooldownUntil
            ? `⏳ Next reward in ${cooldownLabel || '…'}`
            : `📝 Post now — earn ${sound(rates, 'creator_content')}`}
        </div>
        <p className="dashboard-reward-note">Post anytime — earn {sound(rates, 'creator_content')} every 6 hours ({dropSound(rates)} during a live Drop).</p>

        <div className="dashboard-post-card">
            <h2 className="dashboard-post-card-title">What do you want to share?</h2>

            {/* Tab bar */}
            <div className="dashboard-post-tabs">
          {[
            { id: 'text',  label: '✏️ Text'  },
            { id: 'photo', label: '📷 Photo' },
            { id: 'link',  label: '🔗 Link'  },
            { id: 'video', label: '🎬 Video' },
          ].map(t => (
            <button
              key={t.id}
              className={`dashboard-post-tab ${postTab === t.id ? 'dashboard-post-tab--active' : ''}`}
              onClick={() => handleTabChange(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="dashboard-post-body">

          {/* Text tab */}
          {postTab === 'text' && (
            <textarea
              className="dashboard-post-textarea"
              placeholder="What's on your mind? Share an update with your fans…"
              value={postText}
              onChange={e => setPostText(e.target.value.slice(0, 500))}
              rows={4}
            />
          )}

          {/* Photo tab */}
          {postTab === 'photo' && (
            <>
              {postPhotoPreview ? (
                <div className="dashboard-post-media-wrap">
                  <img src={postPhotoPreview} alt="Preview" className="dashboard-post-media-preview" />
                  <button
                    className="dashboard-post-media-remove"
                    onClick={() => { setPostPhoto(null); setPostPhotoPreview(null); }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div
                  className="dashboard-post-upload-zone"
                  onClick={() => postPhotoRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && postPhotoRef.current?.click()}
                >
                  <span className="dashboard-post-upload-icon">📷</span>
                  <span className="dashboard-post-upload-text">Click to add a photo</span>
                  <span className="dashboard-post-upload-sub">JPG, PNG, WebP — max 10 MB</span>
                </div>
              )}
              <input
                ref={postPhotoRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handlePostPhotoPick}
              />
              <textarea
                className="dashboard-post-textarea dashboard-post-textarea--sm"
                placeholder="Caption (optional)"
                value={postText}
                onChange={e => setPostText(e.target.value.slice(0, 280))}
                rows={2}
              />
            </>
          )}

          {/* Link tab */}
          {postTab === 'link' && (
            <input
              className="dashboard-post-link-input"
              type="url"
              placeholder="https://open.spotify.com/track/..."
              value={postLink}
              onChange={e => setPostLink(e.target.value)}
            />
          )}

          {/* Video tab */}
          {postTab === 'video' && (
            <>
              {postVideoPreview ? (
                <div className="dashboard-post-media-wrap">
                  <video
                    src={postVideoPreview}
                    className="dashboard-post-media-preview"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                  <button
                    className="dashboard-post-media-remove"
                    onClick={() => { setPostVideoFile(null); setPostVideoPreview(null); }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div
                  className="dashboard-post-upload-zone"
                  onClick={() => postVideoRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && postVideoRef.current?.click()}
                >
                  <span className="dashboard-post-upload-icon">🎬</span>
                  <span className="dashboard-post-upload-text">Click to add a video</span>
                  <span className="dashboard-post-upload-sub">MP4 or MOV — max 50 MB</span>
                </div>
              )}
              <input
                ref={postVideoRef}
                type="file"
                accept="video/mp4,video/quicktime,video/mov"
                style={{ display: 'none' }}
                onChange={handlePostVideoPick}
              />
              <textarea
                className="dashboard-post-textarea dashboard-post-textarea--sm"
                placeholder="Caption (optional)"
                value={postText}
                onChange={e => setPostText(e.target.value.slice(0, 280))}
                rows={2}
              />
            </>
          )}
        </div>

            {/* Footer */}
            <div className="dashboard-post-footer">
              {postResult?.success && (
                <p className="dashboard-post-result dashboard-post-result--success">
                  ✓ Posted!{postResult.sound > 0 ? ` +${postResult.sound} SOUND` : ' (SOUND cooldown active)'}
                </p>
              )}
              {postResult?.error && (
                <p className="dashboard-post-result dashboard-post-result--error">
                  {postResult.error}
                </p>
              )}
              <button
                className={`dashboard-post-submit ${posting ? 'dashboard-post-submit--loading' : ''}`}
                onClick={handleSubmitPost}
                disabled={posting}
              >
                {posting ? 'Posting…' : 'Post'}
              </button>
            </div>
          </div>
        </div>

      {/* 3b. Street team */}
      <div className="dashboard-section">
        <h2 className="dashboard-section-title">
          Your Street Team
          {streetTeamData && (
            /* Sprint 6-Zero M6 item 3: rendered as "YOUR STREET TEAM2" because
               the count span sat flush against the heading text with no gap.
               The parent uses text-transform: uppercase, which hid it in review. */
            <span className="dashboard-section-count" style={{ marginLeft: '8px' }}>
              {streetTeamData.members?.length || 0}
            </span>
          )}
        </h2>
        {!streetTeamData ? (
          <div className="dashboard-section-loading">Loading…</div>
        ) : streetTeamData.members?.length === 0 ? (
          <div className="dashboard-empty-state">
            <p>No street team yet. Share your referral link to recruit fans.</p>
          </div>
        ) : (
          <>
            {/* Stats row */}
            {/* Sprint 6-Zero M6 item 3: rendered as
                "<value>SOUND EARNED BY TEAM<n>ACTIVE THIS WEEK" — the value and
                label spans were adjacent inline elements with no gap, and the
                stat items themselves ran together. */}
            <div
              className="dashboard-stats-bar"
              style={{ marginBottom: '12px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}
            >
              <div className="dashboard-stat-item" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span className="dashboard-stat-value">
                  {streetTeamData.totalSoundEarned?.toLocaleString()}
                </span>
                <span className="dashboard-stat-label">SOUND earned by team</span>
              </div>
              <div className="dashboard-stat-item" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span className="dashboard-stat-value">
                  {streetTeamData.activeCount}
                </span>
                <span className="dashboard-stat-label">active this week</span>
              </div>
            </div>
            {/* MVP Fan */}
            {streetTeamData.members.some(m => m.is_mvp) && (
              <div style={{
                background: 'rgba(255,215,0,0.1)',
                border: '1px solid rgba(255,215,0,0.3)',
                borderRadius: '8px', padding: '8px 12px',
                marginBottom: '12px', fontSize: '13px'
              }}>
                🏆 MVP Fan this week: {
                  streetTeamData.members.find(m => m.is_mvp)?.display_name
                }
              </div>
            )}
            {/* Member list — top 5 */}
            <div className="dashboard-roster">
              {streetTeamData.members.slice(0, 5).map((member, i) => (
                <div key={`${member.display_name}-${i}`} className="dashboard-roster-item">
                  <div className="roster-avatar roster-avatar--fan">
                    {(member.display_name || '?')[0].toUpperCase()}
                  </div>
                  <div className="roster-info">
                    <p className="roster-name">
                      {member.display_name}
                      {member.is_mvp && ' 🏆'}
                    </p>
                    <p className="roster-rank">
                      {member.rank_title || 'Street Team'}
                      {member.streak_count > 0 && ` · 🔥 ${member.streak_count}d`}
                    </p>
                  </div>
                  {/* Sprint 6-Zero M6 item 4: inactive members shared the
                      roster-badge--fan class and rendered the same green as
                      active ones, so a dead street team looked healthy.
                      Inactive is now muted grey. */}
                  <div
                    className="roster-badge roster-badge--fan"
                    style={member.actionsThisWeek > 0 ? undefined : {
                      background: 'rgba(255,255,255,0.06)',
                      color:      'rgba(255,255,255,0.42)',
                      border:     '1px solid rgba(255,255,255,0.10)',
                    }}
                  >
                    {member.actionsThisWeek > 0
                      ? `${member.actionsThisWeek} actions`
                      : 'Inactive'}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 3c. Drop Scheduler */}
      {creatorDropAt ? (
        <div className="dashboard-section">
          <h2 className="dashboard-section-title">Drop Scheduled 🔥</h2>
          <div className="dashboard-drop-scheduled">
            <p style={{fontWeight:'bold'}}>{creatorDropTitle}</p>
            <p style={{fontSize:'13px', opacity:0.7}}>
              {new Date(creatorDropAt).toLocaleString()}
            </p>
            <button
              className="dashboard-btn dashboard-btn--secondary"
              style={{marginTop:'8px'}}
              onClick={handleCancelDrop}
              disabled={cancellingDrop}
            >
              {cancellingDrop ? 'Cancelling…' : 'Cancel Drop'}
            </button>
          </div>
        </div>
      ) : (
        <div className="dashboard-section">
          <h2 className="dashboard-section-title">Schedule a Drop</h2>
          {creatorStage === 'Burning' || creatorStage === 'Wildfire' || creatorStage === 'Phoenix' ? (
            <div className="dashboard-drop-scheduler">
              {dropStep === 1 && (
                <>
                  <p style={{fontSize:'13px', opacity:0.7, marginBottom:'12px'}}>
                    Schedule a release event. Your street team gets notified.
                    Posts during a Drop earn 3x: {dropSound(rates)} per post for 24 hours.
                  </p>
                  <input
                    type="text"
                    placeholder="Drop title (e.g. 'New single out Friday')"
                    value={dropTitle}
                    onChange={e => setDropTitle(e.target.value)}
                    className="dashboard-input"
                    maxLength={80}
                  />
                  <input
                    type="datetime-local"
                    value={dropScheduledAt}
                    onChange={e => setDropScheduledAt(e.target.value)}
                    className="dashboard-input"
                    style={{marginTop:'8px'}}
                    min={new Date(Date.now() + 3600000).toISOString().slice(0,16)}
                    max={new Date(Date.now() + 7*86400000).toISOString().slice(0,16)}
                  />
                  <button
                    className="dashboard-btn dashboard-btn--primary"
                    style={{marginTop:'8px', width:'100%'}}
                    onClick={() => setDropStep(2)}
                    disabled={!dropTitle.trim() || !dropScheduledAt}
                  >
                    Next: Add content (optional) →
                  </button>
                  <button
                    className="dashboard-btn dashboard-btn--secondary"
                    style={{marginTop:'6px', width:'100%', fontSize:'12px'}}
                    onClick={handleScheduleDrop}
                    disabled={!dropTitle.trim() || !dropScheduledAt || schedulingDrop}
                  >
                    {schedulingDrop ? 'Scheduling…' : 'Skip content → Schedule without content'}
                  </button>
                </>
              )}

              {dropStep === 2 && (
                <div className="drop-content-step">
                  <p style={{fontSize:'13px', opacity:0.7, marginBottom:'12px'}}>
                    Attach content to auto-post when your Drop fires.
                  </p>
                  <div className="drop-type-selector">
                    {['text', 'link', 'photo', 'video'].map(type => (
                      <button
                        key={type}
                        className={`drop-type-btn ${dropContentType === type ? 'active' : ''}`}
                        onClick={() => setDropContentType(type)}
                      >
                        {type === 'text' ? '✍ Text' :
                         type === 'link' ? '🔗 Link' :
                         type === 'photo' ? '📷 Photo' : '🎬 Video'}
                      </button>
                    ))}
                  </div>

                  {dropContentType === 'text' && (
                    <textarea
                      value={dropContentBody}
                      onChange={e => setDropContentBody(e.target.value)}
                      placeholder="What do you want to say when this drops?"
                      maxLength={500}
                      className="dashboard-input"
                      rows={3}
                      style={{marginTop:'8px', width:'100%'}}
                    />
                  )}

                  {dropContentType === 'link' && (
                    <input
                      type="url"
                      value={dropContentBody}
                      onChange={e => setDropContentBody(e.target.value)}
                      placeholder="Spotify, SoundCloud, YouTube link"
                      className="dashboard-input"
                      style={{marginTop:'8px'}}
                    />
                  )}

                  {(dropContentType === 'photo' || dropContentType === 'video') && (
                    <p style={{fontSize:'12px', opacity:0.5, marginTop:'8px'}}>
                      Photo and video upload available soon. Use a link for now.
                    </p>
                  )}

                  <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                    <button
                      className="dashboard-btn dashboard-btn--primary"
                      style={{flex:1}}
                      onClick={handleScheduleDrop}
                      disabled={schedulingDrop}
                    >
                      {schedulingDrop ? 'Scheduling…' : '🔥 Schedule Drop'}
                    </button>
                    <button
                      className="dashboard-btn dashboard-btn--secondary"
                      onClick={() => setDropStep(1)}
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {dropError && (
                <p style={{color:'#ff6b6b', fontSize:'13px', marginTop:'8px'}}>
                  {dropError}
                </p>
              )}
            </div>
          ) : (
            <div className="dashboard-empty-state">
              <p>Drop scheduling unlocks at Burning stage.
                 Keep posting to build your Heat Score.</p>
            </div>
          )}
        </div>
      )}

      {/* 3d. Grow your fanbase */}
      <div className="dashboard-section dashboard-recruit-section">
        <h2 className="dashboard-section-title">🚀 Grow Your Fanbase</h2>
        <p className="dashboard-recruit-desc">
          Share your link. Every fan who signs up earns SOUND points — and you earn <strong>{sound(rates, 'refer_player')}</strong> for each one once they confirm their email and make a first share or scout.
        </p>

        {referralLink ? (
          <>
            <div className="dashboard-referral-link-row">
              <span className="dashboard-referral-link-text">{referralLink}</span>
              <button
                className={`dashboard-referral-copy-btn ${creatorCopyDone ? 'dashboard-referral-copy-btn--done' : ''}`}
                onClick={handleCreatorCopyLink}
              >
                {creatorCopyDone ? '✓ Copied' : 'Copy'}
              </button>
            </div>

            <div className="dashboard-share-row">
              <button
                className="dashboard-share-btn dashboard-share-btn--twitter"
                onClick={handleCreatorShareTwitter}
              >
                <span>𝕏</span> Share on X
              </button>
              <button
                className="dashboard-share-btn dashboard-share-btn--email"
                onClick={handleCreatorShareEmail}
              >
                <span>✉️</span> Share via Email
              </button>
            </div>
          </>
        ) : (
          <p className="dashboard-referral-loading">Loading your referral link…</p>
        )}
      </div>

      {/* 3e. FReN card — already rendered in Zone 2 above */}

      {/* 3f. Video section card */}
      <div className="dashboard-section">
        <h2 className="dashboard-section-title">🎬 Your Video</h2>

        {videoUrl ? (
          <div className="dashboard-video-card">
            <video
              src={videoUrl}
              className="dashboard-video-preview"
              autoPlay
              muted
              loop
              playsInline
            />
            <button
              className="dashboard-video-replace-btn"
              onClick={() => videoInputRef.current?.click()}
              disabled={videoUploading}
            >
              {videoUploading ? '⏳ Uploading…' : '🔄 Replace video'}
            </button>
            {videoUploadDone && (
              <p className="dashboard-video-success">✓ Video updated!</p>
            )}
          </div>
        ) : (
          <div
            className="dashboard-video-upload-zone"
            onClick={() => videoInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && videoInputRef.current?.click()}
          >
            {videoUploading ? (
              <p className="dashboard-video-uploading">⏳ Uploading your video…</p>
            ) : (
              <>
                <div className="dashboard-video-upload-icon">🎬</div>
                <p className="dashboard-video-upload-text">Upload a video</p>
                <p className="dashboard-video-upload-sub">MP4 or MOV — max 50 MB · Earn {sound(rates, 'creator_content')}</p>
              </>
            )}
          </div>
        )}

        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/mov"
          style={{ display: 'none' }}
          onChange={handleVideoPick}
        />

        {videoUploadErr && (
          <p className="dashboard-video-error">{videoUploadErr}</p>
        )}
      </div>

      {/* 3g. AI Missions */}
      <div className="dashboard-section">
        <MissionCard userId={user_id} persona="creator" />
      </div>

      {/* Edit Profile — mobile only (hidden on desktop via CSS) */}
      <button
        className="dashboard-edit-profile-mobile"
        onClick={() => navigate('/creator-onboarding', { state: { isEditMode: true } })}
      >
        ✏️ Edit Profile
      </button>

      {/* Chatbot */}
      <Chatbot ref={chatRef} user={user} session={session} />
    </div>
  );
}
